'use client';
/* External Imports */
import { Box } from '@chakra-ui/react';

/* Internal Imports */
import MultimediaSlider from '@app-ps/Components/Banners/MultimediaSlider';
import ProductionDetails from '@app-ps/Section/ProductionDetails';
import UpcomingList from '@app-ps/Section/UpcomingList';

/**
 * Transforms the production details data to the desired structure for ProductionDetails.
 *
 * @param {Object} details - The original production details.
 * @returns {Array} The transformed production details.
 */
const transformProductionDetails = details => {
  // console.log('details', details);
  const transformedData = [];
  if (details?.length > 0) {
    const item = details[0];

    transformedData.push({
      id: 'register-button',
      buttonStyle: 'Orange',
      buttonText: 'REGISTER NOW',
      linkUrl: item.register_url,
      _modelApiKey: 'simple_button',
      openInNewTab: false,
    });
  }

  return transformedData;
};

/**
 * Transforms the multimedia data to the desired structure for MultimediaSlider.
 *
 * @param {Object} details - The original production details.
 * @returns {Array} The transformed multimedia data.
 */
const transformMultimediaData = details => {
  let multimediaData = {};

  if (details?.length > 0) {
    const item = details[0];

    if (item.samskriti.featured_video || item.samskriti.featured_image) {
      multimediaData = {
        id: item.category_id, // Use a unique ID from your data
        _modelApiKey: 'multimedia_slider',
        title: item.title,
        videoUrl: item.samskriti.featured_video
          .replace(/,/g, '')
          .replace(/"/g, ''), // Remove extra quotes if needed
        style: '', // Add any specific style if needed
        images: Object.assign(
          item.samskriti.featured_image.split(',').map(key => ({
            image: {
              alt: null,
              url: key,
              title: null,
            },
          }))
        ), // Remove extra quotes and trim URLs
      };
    }
  }

  return multimediaData;
};

/**
 * Transforms data to the desired structure for UpcomingList.
 *
 * @param {Object} pId - The original production details.
 * @returns {Array} The transformed UpcomingList data.
 */
const transformUpcomingList = pId => {
  let workshopData = [];

  // if (pId) {
  workshopData = [
    {
      id: 'FtzYPjecRZ-nNC_6CJL9xw',
      titleDecoration: '',
      titleSubtext: 'Offerings',
      titleText: 'Upcoming',
      _modelApiKey: 'section_title',
      titleColor: '',
      additionalTextLine: '',
      style: '',
    },
    {
      id: 'aTfaWsZZSTyVQeCmn53-bg',
      _modelApiKey: 'ps_upcoming_workshop_performance',
      workshopSubtypeFilterVisibility: false,
      style: 'calendar',
      showOnlyScheduledProductionsVisibility: false,
      showOnlyOnlineVisibility: false,
      showOnlyFreeVisibility: false,
      ourRepertoireProductionTypeFiltersVisibility: false,
      defaultCategoryId: '',
      cardCtaLabel: 'Register Now',
      allProductionWorkshopFilterVisibility: false,
      allProductionWorkshopTextLabel: {},
      checkboxTextLabel: {},
      productionTypeFilter: null,
      programTypeList: null,
      scheduleEmptyCta: null,
      scheduleEmptyMessage: {
        value: {
          schema: 'dast',
          document: {
            type: 'root',
            children: [
              {
                type: 'paragraph',
                children: [
                  {
                    type: 'span',
                    value: 'Sorry, there are no scheduled events.',
                  },
                ],
              },
            ],
          },
        },
      },
    },
    {
      id: 'ZpkNzLn_RWydRAPvGm3lWA',
      _modelApiKey: 'simple_link',
      linkUrl: 'calendar',
      subtextDate: '',
      fieldId: '',
      buttonText: 'View All',
    },
  ];
  // }

  return workshopData;
};

/**
 * Renders the RenderProductions component.
 *
 * @param {Array} pageData - Main RenderProductions data.
 * @param {string} region - The current region.
 * @param {string} lang - The current language.
 * @param {Object} productionDetails - The production details data.
 * @returns {ReactElement} RenderProductions component.
 */
const RenderProductions = ({
  region,
  lang,
  productionDetails,
  isScheduled,
  pId,
  bannerSlider,
  detailData,
}) => {
  // Transform the production details data
  // console.log(productionDetails, 'render');
  // console.log(detailData, 'details data');
  const transformedProductionDetails =
    transformProductionDetails(productionDetails);
  const transformedMultimediaData = transformMultimediaData(productionDetails); // Transform multimedia data
  const iprsPgmId = productionDetails[0]?.program_id;
  const transformedWorkshops = transformUpcomingList(iprsPgmId); // Transform UpcomingList data
  return (
    <Box
      fontFamily="var(--FedraSansStd-book)"
      fontSize="1.1rem"
      lineHeight="1.61"
      width="100%"
    >
      <MultimediaSlider
        sectionContent={bannerSlider || transformedMultimediaData}
      />
      <ProductionDetails
        sectionContent={transformedProductionDetails}
        fullDetails={productionDetails}
        pDetails={detailData || productionDetails[0]}
        isScheduled={isScheduled}
        region={region}
        lang={lang}
      />
      {transformedWorkshops && (
        <Box id='ps-prod-upcoming-list' bgColor="#010509">
          <UpcomingList
            sectionContent={transformedWorkshops}
            lang={lang}
            region={region}
          />
        </Box>
      )}
    </Box>
  );
};

export default RenderProductions;
